import { useSelector } from "react-redux";

import { RootState } from "src/redux/reducers";
import { ITask } from "src/redux/task/taskReducer";

type UseGetProjectTaskAutocompleteListResult = ITask[] | null;

/**
 * Custom hook to retrieve task autocomplete list for a specific project.
 *
 * This hook uses selective Redux selection to prevent unnecessary re-renders
 * when unrelated taskReducer fields are updated via websockets.
 *
 * Instead of selecting the entire projectTasksAutocomplete object, it extracts only the
 * autocomplete list for the specified projectId, ensuring the component only re-renders
 * when the specific project's autocomplete data actually changes.
 *
 * @param {string | null} projectId - The UUID of the project to get autocomplete data for, or null
 * @returns {UseGetProjectTaskAutocompleteListResult} Array of ITask objects for the project, or null if not found or projectId is null
 *
 * @example
 * ```tsx
 * const taskAutocompleteList = useGetProjectTaskAutocompleteList(projectId);
 *
 * useEffect(() => {
 *   if (taskAutocompleteList) {
 *     // Use autocomplete list...
 *   }
 * }, [taskAutocompleteList]);
 * ```
 */
export default function useGetProjectTaskAutocompleteList(
  projectId: string | null,
): UseGetProjectTaskAutocompleteListResult {
  return useSelector(
    (state: RootState): UseGetProjectTaskAutocompleteListResult => {
      if (!projectId) return null;

      const projectTasksAutocomplete =
        state.taskReducer.projectTasksAutocomplete;
      const taskAutocompleteList =
        projectTasksAutocomplete?.[projectId] ?? null;

      return taskAutocompleteList;
    },
  );
}
